import { useEffect, useState, useCallback, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";

// Pages
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";
import Shifts from "./pages/Shifts";
import Rules from "./pages/Rules";
import TimeOff from "./pages/TimeOff";
import Operations from "./pages/Operations";
import Preview from "./pages/preview/Preview";
import Signin from "./pages/Signin";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Rosters from "./pages/Rosters";
import RosterDetail from "./pages/RosterDetail";
import Subscription from "./pages/Subscription";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import EmployeePortal from "./pages/EmployeePortal";
import Analytics from "./pages/Analytics";
import Templates from "./pages/Templates";
import Teams from "./pages/Teams";
import ImportRoster from "./pages/ImportRoster";
import AdminLogin from "./pages/AdminLogin";
import AdminLayout from "./pages/AdminLayout";
import AdminDashboard from "./pages/AdminDashboard";
import AdminUsers from "./pages/AdminUsers";
import AdminSubscriptions from "./pages/AdminSubscriptions";
import StaffLogin from "./pages/StaffLogin";
import StaffDashboard from "./pages/StaffDashboard";
import Settings from "./pages/Settings";
import SOPRules from "./pages/SOPRules";
import AdminPromptLogs from "./pages/AdminPromptLogs";
import Contact from "./pages/Contact";
import AdminInbox from "./pages/AdminInbox";
import AdminProfile from "./pages/AdminProfile";
import AdminPayments from "./pages/AdminPayments";
import AdminPricing from "./pages/AdminPricing";
import AdminAISettings from "./pages/AdminAISettings";
import AdminRosterDebug from "./pages/AdminRosterDebug";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Send cookies on every API request (admin_token, staff_token, session_token are httpOnly cookies)
axios.defaults.withCredentials = true;

// Axios interceptor to inject impersonation token (sessionStorage — tab-scoped, dies on tab close)
axios.interceptors.request.use((config) => {
  const impToken = sessionStorage.getItem("impersonate_token");
  if (impToken && config.url?.startsWith(API) && !config.url?.includes('/admin/')) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${impToken}`;
  }
  return config;
});

// Global 402 handler — surface friendly subscription gate errors
let lastSubscriptionToast = 0;
axios.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 402) {
      const now = Date.now();
      if (now - lastSubscriptionToast > 3000) {
        lastSubscriptionToast = now;
        toast.error("Your trial has ended. Please upgrade to continue making changes.", {
          action: {
            label: "Upgrade",
            onClick: () => { window.location.href = "/subscription"; },
          },
        });
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    // Handle admin impersonation token
    const impersonateMatch = window.location.hash?.match(/impersonate=([^&]+)/);
    if (impersonateMatch) {
      const impToken = impersonateMatch[1];
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${impToken}` }
        });
        // Store the impersonation token for future requests (tab-scoped)
        sessionStorage.setItem("impersonate_token", impToken);
        setUser({ ...response.data, _impersonating: true });
        // Clean the hash
        window.history.replaceState(null, '', window.location.pathname);
      } catch (error) {
        sessionStorage.removeItem("impersonate_token");
        setUser(null);
      }
      setLoading(false);
      return;
    }
    
    try {
      // Check for impersonation token in sessionStorage first
      const impToken = sessionStorage.getItem("impersonate_token");
      const config = impToken 
        ? { headers: { Authorization: `Bearer ${impToken}` } }
        : { withCredentials: true };
      const response = await axios.get(`${API}/auth/me`, config);
      setUser(impToken ? { ...response.data, _impersonating: true } : response.data);
    } catch (error) {
      sessionStorage.removeItem("impersonate_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    const impToken = sessionStorage.getItem("impersonate_token");
    if (impToken) {
      // Just clear impersonation, don't call logout API
      sessionStorage.removeItem("impersonate_token");
      setUser(null);
      return;
    }
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          setUser(response.data);
          navigate("/dashboard", { replace: true, state: { user: response.data } });
        } catch (error) {
          console.error("Auth error:", error);
          navigate("/", { replace: true });
        }
      } else {
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-zinc-600 dark:text-zinc-400 text-sm">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // CRITICAL: Check for session_id BEFORE rendering routes
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/preview" element={<Preview />} />
      <Route path="/login" element={<Signin />} />
      <Route path="/signup" element={<Signin />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/portal" element={<EmployeePortal />} />
      <Route path="/staff" element={<StaffLogin />} />
      <Route path="/staff/dashboard" element={<StaffDashboard />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employees"
        element={
          <ProtectedRoute>
            <Employees />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shifts"
        element={
          <ProtectedRoute>
            <Shifts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rules"
        element={
          <ProtectedRoute>
            <Rules />
          </ProtectedRoute>
        }
      />
      <Route
        path="/time-off"
        element={
          <ProtectedRoute>
            <TimeOff />
          </ProtectedRoute>
        }
      />
      <Route
        path="/operations"
        element={
          <ProtectedRoute>
            <Operations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rosters"
        element={
          <ProtectedRoute>
            <Rosters />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rosters/:rosterId"
        element={
          <ProtectedRoute>
            <RosterDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <Subscription />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/success"
        element={
          <ProtectedRoute>
            <SubscriptionSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/teams"
        element={
          <ProtectedRoute>
            <Teams />
          </ProtectedRoute>
        }
      />
      <Route
        path="/import"
        element={
          <ProtectedRoute>
            <ImportRoster />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates"
        element={
          <ProtectedRoute>
            <Templates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sop-rules"
        element={
          <ProtectedRoute>
            <SOPRules />
          </ProtectedRoute>
        }
      />
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="subscriptions" element={<AdminSubscriptions />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="pricing" element={<AdminPricing />} />
        <Route path="roster-debug" element={<AdminRosterDebug />} />
        <Route path="inbox" element={<AdminInbox />} />
        <Route path="profile" element={<AdminProfile />} />
        <Route path="ai-logs" element={<AdminPromptLogs />} />
        <Route path="ai-settings" element={<AdminAISettings />} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
